// Sync type definitions and constants
// Defines the core data structures for the sync system

/**
 * Mutation operation types
 */
export const MutationOperation = {
  CREATE: 'create',
  UPDATE: 'update',
  DELETE: 'delete',
};

/**
 * Mutation status states
 */
export const MutationStatus = {
  PENDING: 'pending',
  PROCESSING: 'processing',
  SYNCED: 'synced',
  FAILED: 'failed',
  CONFLICT: 'conflict',
  DEAD_LETTER: 'dead-letter',
};

/**
 * Conflict resolution strategies
 */
export const ConflictStrategy = {
  LAST_WRITE_WINS: 'last-write-wins',
  SERVER_WINS: 'server-wins',
  CLIENT_MERGE: 'client-merge',
  MANUAL: 'manual',
};

/**
 * Error categories for retry decisions
 */
export const ErrorCategory = {
  TRANSIENT: 'transient', // Retry with backoff
  PERMANENT: 'permanent', // Do not retry
  VALIDATION: 'validation', // Client-side error
  AUTHORIZATION: 'authorization', // Auth error
  CONFLICT: 'conflict', // Requires conflict resolution
};

/**
 * Entity types that require robust sync
 */
export const SyncEntityType = {
  ATHLETES: 'athletes',
  TRAINERS: 'trainers',
  ATTENDANCE: 'attendance',
  PAYMENTS: 'payments',
  DAYBOOKS: 'daybooks',
  MEMBERSHIPS: 'memberships',
  PROGRAMS: 'programs',
  DIETS: 'diets',
};

/**
 * @typedef {Object} Mutation
 * @property {string} mutationId - Unique mutation identifier
 * @property {string} entityType - Type of entity being mutated
 * @property {string} entityId - ID of entity being mutated
 * @property {string} operationType - CREATE, UPDATE, or DELETE
 * @property {Object} payload - Mutation payload
 * @property {string} idempotencyKey - Key for idempotency
 * @property {number} createdAt - Timestamp when mutation was created
 * @property {number} retryCount - Number of retry attempts
 * @property {number} lastAttemptAt - Timestamp of last sync attempt
 * @property {string} status - Current mutation status
 * @property {string[]} [dependencies] - IDs of mutations this depends on
 * @property {Object} [metadata] - Additional metadata
 */

/**
 * @typedef {Object} SyncState
 * @property {boolean} isActive - Whether sync is currently active
 * @property {number} pendingCount - Number of pending mutations
 * @property {number} failedCount - Number of failed mutations
 * @property {number} conflictCount - Number of conflicts
 * @property {number} lastSyncTimestamp - Timestamp of last successful sync
 * @property {string} health - Health status (healthy, degraded, critical)
 */

/**
 * @typedef {Object} ConflictInfo
 * @property {string} mutationId - ID of conflicting mutation
 * @property {string} entityType - Type of entity
 * @property {string} entityId - ID of entity
 * @property {Object} localVersion - Local entity state
 * @property {Object} remoteVersion - Remote entity state
 * @property {string} strategy - Resolution strategy to use
 * @property {string} reason - Reason for conflict
 */

/**
 * @typedef {Object} SyncResult
 * @property {boolean} success - Whether sync was successful
 * @property {string} mutationId - ID of synced mutation
 * @property {Object} [serverResponse] - Server response
 * @property {string} [conflictId] - ID if conflict occurred
 * @property {Error} [error] - Error if failed
 */

/**
 * @typedef {Object} RetryConfig
 * @property {number} maxRetries - Maximum retry attempts
 * @property {number} baseDelay - Base delay in milliseconds
 * @property {number} maxDelay - Maximum delay in milliseconds
 * @property {number} jitterFactor - Jitter factor for randomization
 */

/**
 * @typedef {Object} SyncStats
 * @property {number} totalMutations - Total mutations in queue
 * @property {number} pendingMutations - Pending mutations
 * @property {number} processingMutations - Currently processing
 * @property {number} syncedMutations - Successfully synced
 * @property {number} failedMutations - Failed mutations
 * @property {number} conflictMutations - Conflicts
 * @property {number} deadLetterMutations - Dead letter entries
 * @property {number} avgSyncDuration - Average sync duration
 * @property {number} mutationThroughput - Mutations per minute
 */

/**
 * Default retry configuration
 */
export const DEFAULT_RETRY_CONFIG = {
  maxRetries: 5,
  baseDelay: 2000,
  maxDelay: 30000,
  jitterFactor: 0.3,
};

/**
 * Entity-specific sync priorities
 */
export const SYNC_PRIORITY = {
  [SyncEntityType.ATHLETES]: 1,
  [SyncEntityType.TRAINERS]: 1,
  [SyncEntityType.ATTENDANCE]: 2,
  [SyncEntityType.PAYMENTS]: 1,
  [SyncEntityType.DAYBOOKS]: 2,
  [SyncEntityType.MEMBERSHIPS]: 2,
  [SyncEntityType.PROGRAMS]: 3,
  [SyncEntityType.DIETS]: 3,
};

/**
 * Conflict resolution strategy by entity type
 */
export const ENTITY_CONFLICT_STRATEGY = {
  [SyncEntityType.ATHLETES]: ConflictStrategy.CLIENT_MERGE,
  [SyncEntityType.TRAINERS]: ConflictStrategy.CLIENT_MERGE,
  [SyncEntityType.ATTENDANCE]: ConflictStrategy.SERVER_WINS,
  [SyncEntityType.PAYMENTS]: ConflictStrategy.SERVER_WINS,
  [SyncEntityType.DAYBOOKS]: ConflictStrategy.LAST_WRITE_WINS,
  [SyncEntityType.MEMBERSHIPS]: ConflictStrategy.SERVER_WINS,
  [SyncEntityType.PROGRAMS]: ConflictStrategy.LAST_WRITE_WINS,
  [SyncEntityType.DIETS]: ConflictStrategy.LAST_WRITE_WINS,
};

/**
 * HTTP status codes that indicate transient errors
 */
export const TRANSIENT_ERROR_CODES = [408, 429, 500, 502, 503, 504];

/**
 * HTTP status codes that indicate permanent errors
 */
export const PERMANENT_ERROR_CODES = [400, 401, 403, 404, 422];

/**
 * HTTP status codes that indicate conflicts
 */
export const CONFLICT_ERROR_CODES = [409];
